import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, Car, Bike, Bus, Utensils, Plus, Trash2, Calculator,
  Calendar, MapPin, FileText, RefreshCw, Download, ChevronDown,
  CheckSquare, Square, AlertCircle, TrendingUp, Euro, Fuel, AlertTriangle, Info
} from 'lucide-react';
import { TripDistanceCalculator, calculateMileageCost } from '../components/AddressAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// LIMITES FISCALES 2026
const FISCAL_LIMITS = {
  MEAL_HOME_VALUE: 5.45,
  MEAL_MAX_DEDUCTIBLE: 20.70,
  MEAL_MAX_DEDUCTION: 15.25,  // 20.70 - 5.45
  KM_DOMICILE_TRAVAIL_MAX: 40,
  KM_JOURNALIER_ALERTE: 200,
  KM_ANNUEL_ALERTE: 25000,
  KM_ANNUEL_MAX: 40000
};

const PURPOSE_LABELS = {
  achat_marchandise: "Achat marchandise",
  livraison: "Livraison client",
  intervention_client: "Intervention client",
  depot_retour: "Dépôt/Retour fournisseur",
  domicile_travail: "Domicile-Travail",
  formation: "Formation",
  deplacement: "Déplacement pro",
  autre: "Autre"
};

const TRANSPORT_TYPES = {
  metro: "Métro",
  bus: "Bus",
  train: "Train/TER",
  taxi: "Taxi/VTC",
  autre: "Autre"
};

export default function FraisProPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trips'); // trips, meals, transports, summary
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  
  // Véhicules
  const [vehicles, setVehicles] = useState([]);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    name: '', type: 'voiture', fuel_type: 'thermique', fiscal_power: 5, license_plate: '', is_default: false
  });
  
  // Trajets
  const [trips, setTrips] = useState([]);
  const [showTripModal, setShowTripModal] = useState(false);
  const [newTrip, setNewTrip] = useState({
    date: new Date().toISOString().slice(0, 10),
    vehicle_id: '',
    departure: { address: '', lat: null, lon: null },
    arrival: { address: '', lat: null, lon: null },
    distance_km: '',
    purpose: 'achat_marchandise',
    round_trip: true,
    notes: '',
    calculated_cost: 0
  });
  const [selectedTrips, setSelectedTrips] = useState(new Set());
  
  // Repas
  const [meals, setMeals] = useState([]);
  const [showMealModal, setShowMealModal] = useState(false);
  const [newMeal, setNewMeal] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    location: '',
    purpose: 'deplacement',
    has_restaurant_ticket: false,
    ticket_employer_part: 0,
    notes: ''
  });
  const [selectedMeals, setSelectedMeals] = useState(new Set());
  
  // Transports
  const [transports, setTransports] = useState([]);
  const [showTransportModal, setShowTransportModal] = useState(false);
  const [newTransport, setNewTransport] = useState({
    date: new Date().toISOString().slice(0, 10),
    type: 'metro',
    amount: '',
    departure: '',
    arrival: '',
    purpose: 'achat_marchandise',
    notes: ''
  });
  const [selectedTransports, setSelectedTransports] = useState(new Set());
  
  // Récapitulatif fiscal
  const [taxSummary, setTaxSummary] = useState(null);
  
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadData();
  }, [token, selectedYear, selectedMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [vehiclesRes, tripsRes, mealsRes, transportsRes, summaryRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/frais-pro/vehicles`, { headers }),
        axios.get(`${BACKEND_URL}/api/frais-pro/trips?year=${selectedYear}&month=${selectedMonth}`, { headers }),
        axios.get(`${BACKEND_URL}/api/frais-pro/meals?year=${selectedYear}&month=${selectedMonth}`, { headers }),
        axios.get(`${BACKEND_URL}/api/frais-pro/transports?year=${selectedYear}&month=${selectedMonth}`, { headers }),
        axios.get(`${BACKEND_URL}/api/frais-pro/tax-summary/${selectedYear}`, { headers })
      ]);
      
      setVehicles(vehiclesRes.data);
      setTrips(tripsRes.data);
      setMeals(mealsRes.data);
      setTransports(transportsRes.data);
      setTaxSummary(summaryRes.data);
      
      // Sélectionner le véhicule par défaut
      const defaultVehicle = vehiclesRes.data.find(v => v.is_default);
      if (defaultVehicle) {
        setNewTrip(prev => ({ ...prev, vehicle_id: defaultVehicle.id }));
      }
      
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  // === VÉHICULES ===
  const handleCreateVehicle = async () => {
    if (!newVehicle.name) {
      toast.error('Nom du véhicule requis');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/frais-pro/vehicles`, newVehicle, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Véhicule ajouté');
      setShowVehicleModal(false);
      setNewVehicle({ name: '', type: 'voiture', fuel_type: 'thermique', fiscal_power: 5, license_plate: '', is_default: false });
      loadData();
    } catch (error) {
      toast.error('Erreur création véhicule');
    }
  };

  const handleDeleteVehicle = async (id) => {
    if (!confirm('Supprimer ce véhicule ?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/frais-pro/vehicles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Véhicule supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  // === TRAJETS ===
  const handleCreateTrip = async () => {
    const departureAddr = typeof newTrip.departure === 'string' ? newTrip.departure : newTrip.departure?.address;
    const arrivalAddr = typeof newTrip.arrival === 'string' ? newTrip.arrival : newTrip.arrival?.address;
    
    if (!newTrip.vehicle_id || !departureAddr || !arrivalAddr || !newTrip.distance_km) {
      toast.error('Tous les champs obligatoires doivent être remplis');
      return;
    }
    try {
      const response = await axios.post(`${BACKEND_URL}/api/frais-pro/trips`, {
        date: newTrip.date,
        vehicle_id: newTrip.vehicle_id,
        departure: departureAddr,
        departure_lat: newTrip.departure?.lat || null,
        departure_lon: newTrip.departure?.lon || null,
        arrival: arrivalAddr,
        arrival_lat: newTrip.arrival?.lat || null,
        arrival_lon: newTrip.arrival?.lon || null,
        distance_km: parseFloat(newTrip.distance_km),
        purpose: newTrip.purpose,
        round_trip: newTrip.round_trip,
        notes: newTrip.notes,
        calculated_cost: newTrip.calculated_cost || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Afficher les warnings s'il y en a
      if (response.data.warnings && response.data.warnings.length > 0) {
        response.data.warnings.forEach(w => toast.warning(w, { duration: 8000 }));
      }
      
      toast.success('Trajet enregistré');
      setShowTripModal(false);
      setNewTrip(prev => ({
        ...prev,
        departure: { address: '', lat: null, lon: null },
        arrival: { address: '', lat: null, lon: null },
        distance_km: '',
        notes: '',
        calculated_cost: 0
      }));
      loadData();
    } catch (error) {
      toast.error('Erreur création trajet');
    }
  };

  const handleBulkDeleteTrips = async () => {
    if (selectedTrips.size === 0) return;
    if (!confirm(`Supprimer ${selectedTrips.size} trajet(s) ?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/frais-pro/trips/bulk-delete`, 
        Array.from(selectedTrips),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedTrips.size} trajet(s) supprimé(s)`);
      setSelectedTrips(new Set());
      loadData();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  // === REPAS ===
  const handleCreateMeal = async () => {
    if (!newMeal.amount || !newMeal.location) {
      toast.error('Montant et lieu requis');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/frais-pro/meals`, {
        ...newMeal,
        amount: parseFloat(newMeal.amount),
        ticket_employer_part: parseFloat(newMeal.ticket_employer_part) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Frais de repas enregistré');
      setShowMealModal(false);
      setNewMeal(prev => ({ ...prev, amount: '', location: '', notes: '' }));
      loadData();
    } catch (error) {
      toast.error('Erreur création frais repas');
    }
  };

  const handleBulkDeleteMeals = async () => {
    if (selectedMeals.size === 0) return;
    if (!confirm(`Supprimer ${selectedMeals.size} frais de repas ?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/frais-pro/meals/bulk-delete`,
        Array.from(selectedMeals),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedMeals.size} frais supprimé(s)`);
      setSelectedMeals(new Set());
      loadData();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  // === TRANSPORTS ===
  const handleCreateTransport = async () => {
    if (!newTransport.amount) {
      toast.error('Montant requis');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/frais-pro/transports`, {
        ...newTransport,
        amount: parseFloat(newTransport.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Frais de transport enregistré');
      setShowTransportModal(false);
      loadData();
    } catch (error) {
      toast.error('Erreur création');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/reports')} variant="ghost" size="sm" className="text-white/70 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Calculator className="w-6 h-6 text-green-400" />
                Frais Professionnels
              </h1>
              <p className="text-white/60 text-sm">Kilométrage, repas et transports - Déclaration fiscale</p>
            </div>
          </div>
          
          {/* Filtre période */}
          <div className="flex items-center gap-2">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                  {new Date(2000, i).toLocaleDateString('fr-FR', { month: 'long' })}
                </option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm"
            >
              {[-1, 0, 1].map(offset => {
                const year = new Date().getFullYear() + offset;
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>
          </div>
        </div>

        {/* Stats Cards */}
        {taxSummary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-xl p-4">
              <div className="text-green-300 text-sm mb-1">Total Déductible {selectedYear}</div>
              <div className="text-2xl font-bold text-white">{formatCurrency(taxSummary.summary.total_deductible)}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-4">
              <div className="text-blue-300 text-sm mb-1">Indemnités Kilométriques</div>
              <div className="text-2xl font-bold text-white">{formatCurrency(taxSummary.summary.mileage_allowance)}</div>
              <div className="text-xs text-blue-400">{taxSummary.mileage.total_km} km</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/30 rounded-xl p-4">
              <div className="text-orange-300 text-sm mb-1">Frais Repas Déductibles</div>
              <div className="text-2xl font-bold text-white">{formatCurrency(taxSummary.summary.meals_deductible)}</div>
              <div className="text-xs text-orange-400">{taxSummary.meals.count} repas</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-4">
              <div className="text-purple-300 text-sm mb-1">Transports en Commun</div>
              <div className="text-2xl font-bold text-white">{formatCurrency(taxSummary.summary.transport_total)}</div>
              <div className="text-xs text-purple-400">{taxSummary.transport.count} trajets</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'trips', label: 'Trajets', icon: Car, count: trips.length },
            { id: 'meals', label: 'Repas', icon: Utensils, count: meals.length },
            { id: 'transports', label: 'Transports', icon: Bus, count: transports.length },
            { id: 'vehicles', label: 'Véhicules', icon: Car, count: vehicles.length },
            { id: 'summary', label: 'Récapitulatif Fiscal', icon: FileText }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-green-500 text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          
          {/* === TAB TRAJETS === */}
          {activeTab === 'trips' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Trajets Kilométriques</h2>
                <div className="flex gap-2">
                  {selectedTrips.size > 0 && (
                    <Button onClick={handleBulkDeleteTrips} variant="destructive" size="sm">
                      <Trash2 className="w-4 h-4 mr-2" /> Supprimer ({selectedTrips.size})
                    </Button>
                  )}
                  <Button onClick={() => setShowTripModal(true)} className="bg-green-500 hover:bg-green-600" size="sm">
                    <Plus className="w-4 h-4 mr-2" /> Nouveau trajet
                  </Button>
                </div>
              </div>
              
              {trips.length === 0 ? (
                <div className="text-center py-12 text-white/50">
                  <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun trajet enregistré ce mois</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {trips.map(trip => (
                    <div 
                      key={trip.id}
                      className="flex items-center gap-4 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <button
                        onClick={() => {
                          const newSet = new Set(selectedTrips);
                          if (newSet.has(trip.id)) newSet.delete(trip.id);
                          else newSet.add(trip.id);
                          setSelectedTrips(newSet);
                        }}
                        className="text-white/60 hover:text-white"
                      >
                        {selectedTrips.has(trip.id) ? <CheckSquare className="w-5 h-5 text-green-400" /> : <Square className="w-5 h-5" />}
                      </button>
                      <div className="flex-1">
                        <div className="text-white font-medium">
                          {trip.departure} → {trip.arrival}
                        </div>
                        <div className="text-sm text-white/60">
                          {trip.date} | {trip.vehicle_name} | {PURPOSE_LABELS[trip.purpose] || trip.purpose}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-mono">{trip.total_distance} km</div>
                        <div className="text-xs text-white/50">{trip.round_trip ? 'A/R' : 'Aller simple'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === TAB REPAS === */}
          {activeTab === 'meals' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-white">Frais de Repas</h2>
                  <p className="text-sm text-white/50">Valeur repas domicile 2026: 5,45 €</p>
                </div>
                <div className="flex gap-2">
                  {selectedMeals.size > 0 && (
                    <Button onClick={handleBulkDeleteMeals} variant="destructive" size="sm">
                      <Trash2 className="w-4 h-4 mr-2" /> Supprimer ({selectedMeals.size})
                    </Button>
                  )}
                  <Button onClick={() => setShowMealModal(true)} className="bg-orange-500 hover:bg-orange-600" size="sm">
                    <Plus className="w-4 h-4 mr-2" /> Nouveau repas
                  </Button>
                </div>
              </div>
              
              {meals.length === 0 ? (
                <div className="text-center py-12 text-white/50">
                  <Utensils className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun frais de repas ce mois</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {meals.map(meal => (
                    <div 
                      key={meal.id}
                      className="flex items-center gap-4 p-3 bg-white/5 rounded-lg"
                    >
                      <button
                        onClick={() => {
                          const newSet = new Set(selectedMeals);
                          if (newSet.has(meal.id)) newSet.delete(meal.id);
                          else newSet.add(meal.id);
                          setSelectedMeals(newSet);
                        }}
                        className="text-white/60 hover:text-white"
                      >
                        {selectedMeals.has(meal.id) ? <CheckSquare className="w-5 h-5 text-orange-400" /> : <Square className="w-5 h-5" />}
                      </button>
                      <div className="flex-1">
                        <div className="text-white font-medium">{meal.location}</div>
                        <div className="text-sm text-white/60">
                          {meal.date} | {PURPOSE_LABELS[meal.purpose] || meal.purpose}
                          {meal.has_restaurant_ticket && ' | Ticket resto'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-white">{formatCurrency(meal.amount)}</div>
                        <div className="text-xs text-green-400">Déductible: {formatCurrency(meal.deductible_amount)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === TAB TRANSPORTS === */}
          {activeTab === 'transports' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Transports en Commun</h2>
                <Button onClick={() => setShowTransportModal(true)} className="bg-purple-500 hover:bg-purple-600" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Nouveau transport
                </Button>
              </div>
              
              {transports.length === 0 ? (
                <div className="text-center py-12 text-white/50">
                  <Bus className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun transport en commun ce mois</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {transports.map(t => (
                    <div key={t.id} className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                      <Bus className="w-5 h-5 text-purple-400" />
                      <div className="flex-1">
                        <div className="text-white">{t.departure} → {t.arrival}</div>
                        <div className="text-sm text-white/60">{t.date} | {TRANSPORT_TYPES[t.type] || t.type}</div>
                      </div>
                      <div className="text-white font-mono">{formatCurrency(t.amount)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === TAB VÉHICULES === */}
          {activeTab === 'vehicles' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Mes Véhicules</h2>
                <Button onClick={() => setShowVehicleModal(true)} className="bg-blue-500 hover:bg-blue-600" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter véhicule
                </Button>
              </div>
              
              {vehicles.length === 0 ? (
                <div className="text-center py-12 text-white/50">
                  <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun véhicule enregistré</p>
                  <p className="text-sm mt-2">Ajoutez votre véhicule pour calculer les indemnités kilométriques</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {vehicles.map(v => (
                    <div key={v.id} className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {v.type === 'voiture' ? <Car className="w-5 h-5 text-blue-400" /> : <Bike className="w-5 h-5 text-blue-400" />}
                          <span className="text-white font-medium">{v.name}</span>
                          {v.is_default && <span className="text-xs bg-green-500/30 text-green-300 px-2 py-0.5 rounded">Par défaut</span>}
                        </div>
                        <Button onClick={() => handleDeleteVehicle(v.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-white/70">
                        <div>Type: {v.type}</div>
                        <div>Carburant: {v.fuel_type}</div>
                        <div>Puissance: {v.fiscal_power} CV</div>
                        {v.license_plate && <div>Plaque: {v.license_plate}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === TAB RÉCAPITULATIF FISCAL === */}
          {activeTab === 'summary' && taxSummary && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-white">Récapitulatif Fiscal {selectedYear}</h2>
                <Button variant="outline" size="sm" className="border-white/20 text-white">
                  <Download className="w-4 h-4 mr-2" /> Exporter PDF
                </Button>
              </div>
              
              {/* Encadré Limites Fiscales */}
              <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
                <h3 className="text-amber-300 font-semibold mb-3 flex items-center gap-2">
                  <Info className="w-5 h-5" /> Limites Fiscales 2026
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="text-white font-medium mb-2">Frais de Repas</h4>
                    <ul className="text-white/70 space-y-1">
                      <li>• Valeur repas domicile : <span className="text-amber-300">{FISCAL_LIMITS.MEAL_HOME_VALUE} €</span></li>
                      <li>• Plafond par repas : <span className="text-amber-300">{FISCAL_LIMITS.MEAL_MAX_DEDUCTIBLE} €</span></li>
                      <li>• Déduction max/repas : <span className="text-amber-300">{FISCAL_LIMITS.MEAL_MAX_DEDUCTION} €</span></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-2">Kilométrage</h4>
                    <ul className="text-white/70 space-y-1">
                      <li>• Domicile-travail max : <span className="text-amber-300">{FISCAL_LIMITS.KM_DOMICILE_TRAVAIL_MAX} km</span> (aller)</li>
                      <li>• Alerte journalière : <span className="text-amber-300">{FISCAL_LIMITS.KM_JOURNALIER_ALERTE} km</span></li>
                      <li>• Seuil annuel alerte : <span className="text-amber-300">{FISCAL_LIMITS.KM_ANNUEL_ALERTE.toLocaleString()} km</span></li>
                      <li>• Seuil critique : <span className="text-red-300">{FISCAL_LIMITS.KM_ANNUEL_MAX.toLocaleString()} km</span></li>
                    </ul>
                  </div>
                </div>
                <p className="text-xs text-white/50 mt-3">
                  Source: economie.gouv.fr, impots.gouv.fr | Conservez tous vos justificatifs en cas de contrôle fiscal.
                </p>
              </div>
              
              {/* Alertes si limites dépassées */}
              {taxSummary.mileage.total_km > FISCAL_LIMITS.KM_ANNUEL_ALERTE && (
                <div className={`p-4 rounded-xl mb-6 border ${
                  taxSummary.mileage.total_km > FISCAL_LIMITS.KM_ANNUEL_MAX 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : 'bg-amber-500/10 border-amber-500/30'
                }`}>
                  <div className="flex items-center gap-3">
                    <AlertTriangle className={`w-6 h-6 ${
                      taxSummary.mileage.total_km > FISCAL_LIMITS.KM_ANNUEL_MAX ? 'text-red-400' : 'text-amber-400'
                    }`} />
                    <div>
                      <div className={`font-medium ${
                        taxSummary.mileage.total_km > FISCAL_LIMITS.KM_ANNUEL_MAX ? 'text-red-300' : 'text-amber-300'
                      }`}>
                        {taxSummary.mileage.total_km > FISCAL_LIMITS.KM_ANNUEL_MAX 
                          ? 'Kilométrage annuel critique !' 
                          : 'Kilométrage annuel élevé'}
                      </div>
                      <div className="text-sm text-white/70">
                        {taxSummary.mileage.total_km.toLocaleString()} km déclarés - 
                        {taxSummary.mileage.total_km > FISCAL_LIMITS.KM_ANNUEL_MAX 
                          ? ' Risque de contrôle fiscal élevé. Préparez vos justificatifs.' 
                          : ' Approche du seuil de vigilance. Gardez vos justificatifs.'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-green-300 mb-4">Total Frais Réels Déductibles</h3>
                <div className="text-4xl font-bold text-white mb-2">{formatCurrency(taxSummary.summary.total_deductible)}</div>
                <p className="text-sm text-white/60">{taxSummary.note}</p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                {/* Kilométrique */}
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="text-blue-300 font-medium mb-3 flex items-center gap-2">
                    <Car className="w-4 h-4" /> Indemnités Kilométriques
                  </h4>
                  <div className="text-2xl font-bold text-white mb-2">{formatCurrency(taxSummary.summary.mileage_allowance)}</div>
                  <div className="text-sm text-white/60 space-y-1">
                    <div>Total: {taxSummary.mileage.total_km} km</div>
                    <div>{taxSummary.mileage.trip_count} trajets</div>
                  </div>
                  {taxSummary.mileage.by_vehicle?.map((v, i) => (
                    <div key={i} className="mt-3 p-2 bg-white/5 rounded text-sm">
                      <div className="text-white">{v.vehicle_name}</div>
                      <div className="text-white/60">{v.total_km} km → {formatCurrency(v.allowance)}</div>
                      <div className="text-xs text-white/40">Formule: {v.formula}</div>
                    </div>
                  ))}
                </div>
                
                {/* Repas */}
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="text-orange-300 font-medium mb-3 flex items-center gap-2">
                    <Utensils className="w-4 h-4" /> Frais de Repas
                  </h4>
                  <div className="text-2xl font-bold text-white mb-2">{formatCurrency(taxSummary.summary.meals_deductible)}</div>
                  <div className="text-sm text-white/60 space-y-1">
                    <div>{taxSummary.meals.count} repas</div>
                    <div>Coût total: {formatCurrency(taxSummary.meals.total_cost)}</div>
                    <div>Valeur repas domicile: {taxSummary.meals.home_meal_value_used}€</div>
                  </div>
                </div>
                
                {/* Transports */}
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="text-purple-300 font-medium mb-3 flex items-center gap-2">
                    <Bus className="w-4 h-4" /> Transports en Commun
                  </h4>
                  <div className="text-2xl font-bold text-white mb-2">{formatCurrency(taxSummary.summary.transport_total)}</div>
                  <div className="text-sm text-white/60">
                    {taxSummary.transport.count} trajets
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* === MODAL NOUVEAU VÉHICULE === */}
      {showVehicleModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">Nouveau Véhicule</h3>
            <div className="space-y-4">
              <div>
                <label className="text-white/70 text-sm">Nom du véhicule *</label>
                <Input
                  value={newVehicle.name}
                  onChange={(e) => setNewVehicle({ ...newVehicle, name: e.target.value })}
                  placeholder="Ex: Peugeot 308"
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/70 text-sm">Type</label>
                  <select
                    value={newVehicle.type}
                    onChange={(e) => setNewVehicle({ ...newVehicle, type: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white"
                  >
                    <option value="voiture">Voiture</option>
                    <option value="moto">Moto (&gt;50cc)</option>
                    <option value="cyclomoteur">Cyclomoteur (≤50cc)</option>
                  </select>
                </div>
                <div>
                  <label className="text-white/70 text-sm">Carburant</label>
                  <select
                    value={newVehicle.fuel_type}
                    onChange={(e) => setNewVehicle({ ...newVehicle, fuel_type: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white"
                  >
                    <option value="thermique">Thermique/Hybride</option>
                    <option value="electrique">100% Électrique</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/70 text-sm">Puissance fiscale (CV) *</label>
                  <Input
                    type="number"
                    value={newVehicle.fiscal_power}
                    onChange={(e) => setNewVehicle({ ...newVehicle, fiscal_power: parseInt(e.target.value) || 1 })}
                    min="1"
                    max="20"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <label className="text-white/70 text-sm">Plaque (optionnel)</label>
                  <Input
                    value={newVehicle.license_plate}
                    onChange={(e) => setNewVehicle({ ...newVehicle, license_plate: e.target.value })}
                    placeholder="AA-123-BB"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-white/70">
                <input
                  type="checkbox"
                  checked={newVehicle.is_default}
                  onChange={(e) => setNewVehicle({ ...newVehicle, is_default: e.target.checked })}
                  className="rounded"
                />
                Véhicule par défaut
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowVehicleModal(false)} variant="outline" className="flex-1 border-white/20 text-white">
                Annuler
              </Button>
              <Button onClick={handleCreateVehicle} className="flex-1 bg-blue-500 hover:bg-blue-600">
                Ajouter
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* === MODAL NOUVEAU TRAJET === */}
      {showTripModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-400" />
              Nouveau Trajet
            </h3>
            <div className="space-y-4">
              {/* Date et Véhicule */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/70 text-sm">Date *</label>
                  <Input
                    type="date"
                    value={newTrip.date}
                    onChange={(e) => setNewTrip({ ...newTrip, date: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <label className="text-white/70 text-sm">Véhicule *</label>
                  <select
                    value={newTrip.vehicle_id}
                    onChange={(e) => setNewTrip({ ...newTrip, vehicle_id: e.target.value })}
                    className="w-full h-10 bg-white/10 border border-white/20 rounded px-3 py-2 text-white"
                  >
                    <option value="">Sélectionner</option>
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id}>{v.name} ({v.fiscal_power} CV)</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Calculateur de trajet avec autocomplétion */}
              <TripDistanceCalculator
                departure={newTrip.departure}
                setDeparture={(val) => setNewTrip({ ...newTrip, departure: val })}
                arrival={newTrip.arrival}
                setArrival={(val) => setNewTrip({ ...newTrip, arrival: val })}
                distance={parseFloat(newTrip.distance_km) || 0}
                setDistance={(val) => setNewTrip({ ...newTrip, distance_km: val.toString() })}
                roundTrip={newTrip.round_trip}
                setRoundTrip={(val) => setNewTrip({ ...newTrip, round_trip: val })}
                vehicle={vehicles.find(v => v.id === newTrip.vehicle_id)}
                yearlyKm={taxSummary?.mileage?.total_km || 0}
                onCostCalculated={(cost) => setNewTrip(prev => ({ ...prev, calculated_cost: cost }))}
              />

              {/* Motif */}
              <div>
                <label className="text-white/70 text-sm">Motif du déplacement</label>
                <select
                  value={newTrip.purpose}
                  onChange={(e) => setNewTrip({ ...newTrip, purpose: e.target.value })}
                  className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white"
                >
                  {Object.entries(PURPOSE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="text-white/70 text-sm">Notes (optionnel)</label>
                <Input
                  value={newTrip.notes}
                  onChange={(e) => setNewTrip({ ...newTrip, notes: e.target.value })}
                  placeholder="Ex: Récupération écrans iPhone"
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowTripModal(false)} variant="outline" className="flex-1 border-white/20 text-white">
                Annuler
              </Button>
              <Button onClick={handleCreateTrip} className="flex-1 bg-green-500 hover:bg-green-600">
                <Calculator className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* === MODAL NOUVEAU REPAS === */}
      {showMealModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">Nouveau Frais de Repas</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/70 text-sm">Date *</label>
                  <Input
                    type="date"
                    value={newMeal.date}
                    onChange={(e) => setNewMeal({ ...newMeal, date: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <label className="text-white/70 text-sm">Montant payé (€) *</label>
                  <Input
                    type="number"
                    value={newMeal.amount}
                    onChange={(e) => setNewMeal({ ...newMeal, amount: e.target.value })}
                    placeholder="15.50"
                    step="0.01"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="text-white/70 text-sm">Lieu *</label>
                <Input
                  value={newMeal.location}
                  onChange={(e) => setNewMeal({ ...newMeal, location: e.target.value })}
                  placeholder="Ex: Restaurant Le Comptoir"
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
              <div>
                <label className="text-white/70 text-sm">Motif</label>
                <select
                  value={newMeal.purpose}
                  onChange={(e) => setNewMeal({ ...newMeal, purpose: e.target.value })}
                  className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white"
                >
                  {Object.entries(PURPOSE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-white/70">
                <input
                  type="checkbox"
                  checked={newMeal.has_restaurant_ticket}
                  onChange={(e) => setNewMeal({ ...newMeal, has_restaurant_ticket: e.target.checked })}
                  className="rounded"
                />
                J'ai utilisé des tickets restaurant
              </label>
              {newMeal.has_restaurant_ticket && (
                <div>
                  <label className="text-white/70 text-sm">Part employeur du ticket (€)</label>
                  <Input
                    type="number"
                    value={newMeal.ticket_employer_part}
                    onChange={(e) => setNewMeal({ ...newMeal, ticket_employer_part: e.target.value })}
                    placeholder="5.00"
                    step="0.01"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
              )}
              
              {/* Encadré limites fiscales repas */}
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs">
                <div className="text-amber-300 font-medium mb-1">Limites fiscales 2026</div>
                <div className="text-white/60">
                  • Valeur repas domicile: {FISCAL_LIMITS.MEAL_HOME_VALUE}€ | Plafond: {FISCAL_LIMITS.MEAL_MAX_DEDUCTIBLE}€ | Déduction max: {FISCAL_LIMITS.MEAL_MAX_DEDUCTION}€
                </div>
              </div>
              
              {/* Calcul de la déduction avec plafond */}
              {(() => {
                const amount = parseFloat(newMeal.amount) || 0;
                const ticketPart = newMeal.has_restaurant_ticket ? parseFloat(newMeal.ticket_employer_part) || 0 : 0;
                const effectiveCost = Math.min(amount, FISCAL_LIMITS.MEAL_MAX_DEDUCTIBLE);
                const deduction = Math.max(0, Math.min(effectiveCost - FISCAL_LIMITS.MEAL_HOME_VALUE - ticketPart, FISCAL_LIMITS.MEAL_MAX_DEDUCTION));
                const isCapped = amount > FISCAL_LIMITS.MEAL_MAX_DEDUCTIBLE;
                
                return (
                  <div className={`p-3 rounded-lg text-sm border ${isCapped ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                    <div className={isCapped ? 'text-red-300' : 'text-green-300'}>Calcul de la déduction:</div>
                    {isCapped && (
                      <div className="text-red-400 text-xs mb-1">
                        ⚠️ Montant plafonné à {FISCAL_LIMITS.MEAL_MAX_DEDUCTIBLE}€
                      </div>
                    )}
                    <div className="text-white/70">
                      {isCapped ? FISCAL_LIMITS.MEAL_MAX_DEDUCTIBLE : amount}€ - {FISCAL_LIMITS.MEAL_HOME_VALUE}€ (repas domicile)
                      {ticketPart > 0 && ` - ${ticketPart}€ (ticket resto)`}
                      = <span className="text-green-400 font-medium">{deduction.toFixed(2)}€ déductibles</span>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowMealModal(false)} variant="outline" className="flex-1 border-white/20 text-white">
                Annuler
              </Button>
              <Button onClick={handleCreateMeal} className="flex-1 bg-orange-500 hover:bg-orange-600">
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* === MODAL NOUVEAU TRANSPORT === */}
      {showTransportModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">Nouveau Transport en Commun</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/70 text-sm">Date *</label>
                  <Input
                    type="date"
                    value={newTransport.date}
                    onChange={(e) => setNewTransport({ ...newTransport, date: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <label className="text-white/70 text-sm">Type</label>
                  <select
                    value={newTransport.type}
                    onChange={(e) => setNewTransport({ ...newTransport, type: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white"
                  >
                    {Object.entries(TRANSPORT_TYPES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-white/70 text-sm">Montant (€) *</label>
                <Input
                  type="number"
                  value={newTransport.amount}
                  onChange={(e) => setNewTransport({ ...newTransport, amount: e.target.value })}
                  placeholder="2.10"
                  step="0.01"
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/70 text-sm">Départ</label>
                  <Input
                    value={newTransport.departure}
                    onChange={(e) => setNewTransport({ ...newTransport, departure: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <label className="text-white/70 text-sm">Arrivée</label>
                  <Input
                    value={newTransport.arrival}
                    onChange={(e) => setNewTransport({ ...newTransport, arrival: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowTransportModal(false)} variant="outline" className="flex-1 border-white/20 text-white">
                Annuler
              </Button>
              <Button onClick={handleCreateTransport} className="flex-1 bg-purple-500 hover:bg-purple-600">
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
